import { AccountVariableType } from '@/client/accounts';
import { AppRoutesPaths } from '@/common/constants/routes';
import {
  POINT_OF_CONTACT_FORMAT,
  POINT_OF_CONTACT_TYPE_ATTRIBUTE,
} from '@/utils/helpers/point-of-contact.helper';
import { t } from 'i18next';
import Quill from 'quill';
import type React from 'react';

const Inline = Quill.import('blots/inline');

class PointOfContactBlot extends Inline {
  static create(value: string) {
    const node = super.create();
    node.classList.add(POINT_OF_CONTACT_FORMAT);
    node.setAttribute(POINT_OF_CONTACT_TYPE_ATTRIBUTE, value);
    return node;
  }

  static formats(node: HTMLElement) {
    return node.getAttribute(POINT_OF_CONTACT_TYPE_ATTRIBUTE);
  }

  format(name: string, value: string | boolean) {
    if (name !== POINT_OF_CONTACT_FORMAT) {
      super.format(name, value);
      return;
    }

    if (value && typeof value === 'string') {
      this.domNode.setAttribute(POINT_OF_CONTACT_TYPE_ATTRIBUTE, value);
      return;
    }

    this.domNode.removeAttribute(POINT_OF_CONTACT_TYPE_ATTRIBUTE);
    this.domNode.classList.remove(POINT_OF_CONTACT_FORMAT);
    if (this.domNode.classList.length === 0) {
      this.domNode.removeAttribute('class');
    }

    const attributeCount = this.domNode.attributes.length;
    const classCount = this.domNode.classList.length;

    if (attributeCount === 0 && classCount === 0) {
      super.format(name, false);
    }
  }
}

PointOfContactBlot.blotName = POINT_OF_CONTACT_FORMAT;
PointOfContactBlot.tagName = 'span';
// The class registration is load-bearing twice over: saved HTML is parsed back
// through the clipboard matchers, which resolve a <span> by class before tag,
// and a span blot registered without a class would take over the generic SPAN
// mapping that TooltipBlot relies on for its own round-trip.
PointOfContactBlot.className = POINT_OF_CONTACT_FORMAT;
Quill.register(PointOfContactBlot);

type PointOfContactOption = {
  type: AccountVariableType;
  labelKey: string;
};

const POINT_OF_CONTACT_OPTIONS: PointOfContactOption[] = [
  {
    type: AccountVariableType.IT_CONTACT,
    labelKey: 'course.pointOfContact.itContact',
  },
];

type PointOfContactButtonOptions = {
  isConfigured: boolean;
  canConfigure: boolean;
};

const POINT_OF_CONTACT_BUTTON_OPEN_CLASS = 'point-of-contact-btn--open';

let outsideClickHandler: ((event: MouseEvent) => void) | null = null;

const TOOLTIP_FORMAT = 'tooltip';

export const clearPointOfContact = (
  editor: any,
  index: number,
  length: number,
): void => {
  if (!editor || length <= 0) return;
  editor.formatText(index, length, POINT_OF_CONTACT_FORMAT, false, 'user');
};

const togglePointOfContact = (editor: any, type: AccountVariableType) => {
  if (!editor) return;

  const range = editor.getSelection(true);
  if (!range || range.length === 0) return;

  const currentType = editor.getFormat(range)[POINT_OF_CONTACT_FORMAT];
  const isRemoving = currentType === type;

  editor.formatText(
    range.index,
    range.length,
    isRemoving
      ? { [POINT_OF_CONTACT_FORMAT]: false }
      : { [TOOLTIP_FORMAT]: false, [POINT_OF_CONTACT_FORMAT]: type },
    'user',
  );
  editor.setSelection(range.index, range.length, 'silent');
};

export const setupPointOfContactButton = (
  quillRef: React.RefObject<any>,
  button: HTMLButtonElement | null,
  options: PointOfContactButtonOptions,
): void => {
  if (!button) return;

  const formatsContainer = button.parentElement;
  if (!formatsContainer) return;

  formatsContainer.querySelector('.point-of-contact-dropdown')?.remove();
  if (outsideClickHandler) {
    document.removeEventListener('mousedown', outsideClickHandler);
    outsideClickHandler = null;
  }

  const dropdown = document.createElement('div');
  dropdown.className = 'point-of-contact-dropdown';
  dropdown.style.display = 'none';

  const setDropdownOpen = (isOpen: boolean): void => {
    dropdown.style.display = isOpen ? 'block' : 'none';
    button.classList.toggle(POINT_OF_CONTACT_BUTTON_OPEN_CLASS, isOpen);
  };

  for (const option of POINT_OF_CONTACT_OPTIONS) {
    const row = document.createElement('div');
    row.className = 'point-of-contact-dropdown-row';
    row.setAttribute('role', 'button');
    row.setAttribute('tabindex', '0');

    const rowLabel = document.createElement('span');
    rowLabel.className = 'point-of-contact-dropdown-label';
    rowLabel.textContent = t(option.labelKey);

    row.appendChild(rowLabel);

    const applyOption = () => {
      setDropdownOpen(false);
      togglePointOfContact(quillRef.current, option.type);
    };

    // mousedown + preventDefault: a click would blur the editor and collapse
    // the range before the format could be applied to it.
    row.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      applyOption();
    });

    row.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      e.stopPropagation();
      applyOption();
    });

    dropdown.appendChild(row);
  }

  if (!options.isConfigured && options.canConfigure) {
    const hint = document.createElement('a');
    hint.className = 'point-of-contact-dropdown-hint';
    hint.href = AppRoutesPaths.ACCOUNT_TRAINING_SETTINGS_ROUTE;
    hint.target = '_blank';
    hint.rel = 'noopener noreferrer';
    hint.textContent = t('course.pointOfContact.notConfigured');
    dropdown.appendChild(hint);
  }

  button.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownOpen(dropdown.style.display === 'none');
  };

  outsideClickHandler = (e: MouseEvent) => {
    if (!formatsContainer.contains(e.target as Node)) {
      setDropdownOpen(false);
    }
  };
  document.addEventListener('mousedown', outsideClickHandler);

  formatsContainer.appendChild(dropdown);
};
